import { TWO_PI, SECONDS_PER_DAY, RAD2DEG } from "./constants";

/**
 * Keplerian orbital elements. All angles in radians.
 *
 * These six classical elements, plus an epoch, fully describe a
 * two-body Keplerian orbit.
 */
export interface OrbitalElements {
  /** Semi-major axis (km) */
  semiMajorAxis: number;

  /** Eccentricity (dimensionless, 0 = circular, <1 = elliptical) */
  eccentricity: number;

  /** Inclination (radians) */
  inclination: number;

  /** Longitude of ascending node / RAAN (radians) */
  longitudeOfAscendingNode: number;

  /** Argument of periapsis (radians) */
  argumentOfPeriapsis: number;

  /** Mean anomaly at epoch (radians) */
  meanAnomalyAtEpoch: number;

  /** Reference epoch (Julian Date) */
  epoch: number;

  /** Name of the central body */
  centralBody: string;
}

// --- Derived quantities as pure functions ---

/** Periapsis distance (km). */
export function periapsis(el: OrbitalElements): number {
  return el.semiMajorAxis * (1.0 - el.eccentricity);
}

/** Apoapsis distance (km). Only valid for elliptical orbits (e < 1). */
export function apoapsis(el: OrbitalElements): number {
  return el.semiMajorAxis * (1.0 + el.eccentricity);
}

/** Semi-latus rectum p = a(1 - e^2) (km). */
export function semiLatusRectum(el: OrbitalElements): number {
  return el.semiMajorAxis * (1.0 - el.eccentricity * el.eccentricity);
}

/**
 * Orbital period (seconds).
 * T = 2*pi * sqrt(a^3 / GM)
 *
 * @param el Orbital elements
 * @param gm Gravitational parameter of central body (km^3/s^2)
 */
export function period(el: OrbitalElements, gm: number): number {
  if (el.semiMajorAxis <= 0) return Infinity;
  return TWO_PI * Math.sqrt(
    el.semiMajorAxis * el.semiMajorAxis * el.semiMajorAxis / gm,
  );
}

/**
 * Mean motion n = sqrt(GM / |a|^3) (radians/second).
 * Works for both elliptical (a > 0) and hyperbolic (a < 0) orbits.
 *
 * @param el Orbital elements
 * @param gm Gravitational parameter of central body (km^3/s^2)
 */
export function meanMotion(el: OrbitalElements, gm: number): number {
  const absA = Math.abs(el.semiMajorAxis);
  if (absA < 1e-10) return 0;
  return Math.sqrt(gm / (absA * absA * absA));
}

/**
 * Mean motion (radians/day).
 *
 * @param el Orbital elements
 * @param gm Gravitational parameter of central body (km^3/s^2)
 */
export function meanMotionPerDay(el: OrbitalElements, gm: number): number {
  return meanMotion(el, gm) * SECONDS_PER_DAY;
}

/**
 * Compute mean anomaly at a given Julian Date by propagating
 * from the epoch using mean motion.
 *
 * M(t) = M0 + n * (t - t0)
 *
 * Result is normalized to [0, 2*pi).
 *
 * @param el Orbital elements
 * @param jd Julian Date
 * @param gm Gravitational parameter (km^3/s^2)
 */
export function meanAnomalyAt(el: OrbitalElements, jd: number, gm: number): number {
  const dt = (jd - el.epoch) * SECONDS_PER_DAY;
  const n = meanMotion(el, gm);
  let M = el.meanAnomalyAtEpoch + n * dt;
  // Only normalize for elliptical orbits; hyperbolic M grows unbounded
  if (el.eccentricity < 1.0) {
    M = M % TWO_PI;
    if (M < 0) M += TWO_PI;
  }
  return M;
}

/** Human-readable string representation. */
export function elementsToString(el: OrbitalElements): string {
  return (
    `a=${el.semiMajorAxis.toFixed(1)}km ` +
    `e=${el.eccentricity.toFixed(6)} ` +
    `i=${(el.inclination * RAD2DEG).toFixed(3)}deg ` +
    `LAN=${(el.longitudeOfAscendingNode * RAD2DEG).toFixed(3)}deg ` +
    `AoP=${(el.argumentOfPeriapsis * RAD2DEG).toFixed(3)}deg ` +
    `M0=${(el.meanAnomalyAtEpoch * RAD2DEG).toFixed(3)}deg`
  );
}
